import React, { useState } from 'react';
import { Plan } from '../../types';
import { useAMedias } from '../../context/AMediasContext';
import { usePlus } from '../../context/PlusContext';
import { Plus, X } from 'lucide-react';
import { Campo, claseInput, aInput, deInput, MensajeError, ModalBase, BotonesModal } from './ui';
import { PLAZOS_RECORDATORIO, PLAZO_POR_DEFECTO } from '../../utils/recordatoriosAMedias';

export const ICONOS_PLAN = ['✈️', '🏖️', '🏔️', '⛺', '🚗', '🍽️', '🍻', '🎉', '🎂', '🏠', '🎿', '🧾'];

interface Props {
  plan?: Plan;
  onClose: () => void;
  /** Tras crear uno nuevo, con su id (para entrar en él). */
  onCreado?: (planId: string) => void;
}

/**
 * Datos básicos de un plan. Al crearlo se apuntan también los demás
 * participantes; después se gestionan desde los ajustes del plan.
 */
export const ModalPlan: React.FC<Props> = ({ plan, onClose, onCreado }) => {
  const { crearPlan, actualizarPlan } = useAMedias();
  const { ofrecerSiEsLimite } = usePlus();

  const [nombre, setNombre] = useState(plan?.nombre ?? '');
  const [icono, setIcono] = useState(plan?.icono ?? ICONOS_PLAN[0]);
  const [descripcion, setDescripcion] = useState(plan?.descripcion ?? '');
  const [fechaInicio, setFechaInicio] = useState(aInput(plan?.fechaInicio));
  const [fechaFin, setFechaFin] = useState(aInput(plan?.fechaFin));
  const [otros, setOtros] = useState<string[]>(['']);
  const [recordatorioDias, setRecordatorioDias] = useState(plan?.recordatorioDias ?? PLAZO_POR_DEFECTO);
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cambiarOtro = (i: number, v: string) => setOtros(prev => prev.map((x, j) => (j === i ? v : x)));

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nombre.trim()) { setError('Ponle un nombre al plan.'); return; }
    if (fechaInicio && fechaFin && fechaFin < fechaInicio) {
      setError('La fecha de fin no puede ser anterior a la de inicio.'); return;
    }
    const nombres = otros.map(o => o.trim()).filter(Boolean);
    const repetido = nombres.find((n, i) => nombres.findIndex(m => m.toLowerCase() === n.toLowerCase()) !== i);
    if (repetido) { setError(`«${repetido}» está repetido.`); return; }

    setGuardando(true);
    setError('');
    const datos = {
      nombre: nombre.trim(),
      icono,
      descripcion: descripcion.trim() || undefined,
      fechaInicio: deInput(fechaInicio),
      fechaFin: deInput(fechaFin),
      recordatorioDias,
    };
    try {
      if (plan) {
        await actualizarPlan(plan.id, datos);
        onClose();
      } else {
        const id = await crearPlan(datos, nombres);
        onClose();
        onCreado?.(id);
      }
    } catch (err) {
      if (ofrecerSiEsLimite(err)) { onClose(); return; }
      console.error(err);
      setError('No se pudo guardar. Inténtalo de nuevo.');
      setGuardando(false);
    }
  };

  return (
    <ModalBase titulo={plan ? 'Editar plan' : 'Nuevo plan'} onClose={onClose}>
      <form onSubmit={enviar} className="p-5 space-y-4">
        <MensajeError texto={error} />

        <Campo label="Nombre *">
          <input type="text" value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Asturias agosto, cena de Navidad, piso…" className={claseInput} autoFocus />
        </Campo>

        <Campo label="Icono">
          <div className="flex flex-wrap gap-1.5">
            {ICONOS_PLAN.map(i => (
              <button key={i} type="button" onClick={() => setIcono(i)} aria-pressed={icono === i}
                className={`w-10 h-10 rounded-lg text-xl border transition ${
                  icono === i
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                }`}>
                {i}
              </button>
            ))}
          </div>
        </Campo>

        <div className="grid gap-3 grid-cols-2">
          <Campo label="Desde">
            <input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className={claseInput} />
          </Campo>
          <Campo label="Hasta">
            <input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} className={claseInput} />
          </Campo>
        </div>

        <Campo label="Recordar a quien deba"
          ayuda="Cuenta desde la fecha de fin del plan o, si no tiene, desde el último gasto. A quien deba le saldrá un aviso en la app.">
          <select value={recordatorioDias} onChange={e => setRecordatorioDias(Number(e.target.value))} className={claseInput}>
            {PLAZOS_RECORDATORIO.map(d => (
              <option key={d} value={d}>{d === 0 ? 'No recordar' : `A los ${d} días`}</option>
            ))}
          </select>
        </Campo>

        <Campo label="Descripción">
          <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} rows={2}
            placeholder="Opcional" className={`${claseInput} resize-y`} />
        </Campo>

        {!plan && (
          <Campo label="¿Quién más participa?"
            ayuda="Tú ya estás incluido. Más adelante puedes añadir o quitar gente.">
            <div className="space-y-2">
              {otros.map((o, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={o} onChange={e => cambiarOtro(i, e.target.value)}
                    onKeyDown={e => {
                      // Intro en el último añade otra fila en vez de enviar el formulario
                      if (e.key === 'Enter' && i === otros.length - 1 && o.trim()) {
                        e.preventDefault();
                        setOtros(prev => [...prev, '']);
                      }
                    }}
                    placeholder="Nombre" className={claseInput} />
                  {otros.length > 1 && (
                    <button type="button" onClick={() => setOtros(prev => prev.filter((_, j) => j !== i))}
                      aria-label="Quitar" className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50
                                                     dark:hover:bg-red-900/20 transition">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => setOtros(prev => [...prev, ''])}
                className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 dark:text-emerald-400
                           hover:underline">
                <Plus className="w-4 h-4" /> Otra persona
              </button>
            </div>
          </Campo>
        )}

        <BotonesModal guardando={guardando} texto={plan ? 'Guardar cambios' : 'Crear plan'} onClose={onClose} />
      </form>
    </ModalBase>
  );
};
